// analytics/hooks/useOutpatientsData.ts
import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';  // Import useSelector instead of connect
import { 
  outpatientServices 
} from '../services/endpoints/outpatients';
import { OutPatientResponse, DiagnosisResponse, ActivityPatientsDistributionResponse, ChartData, DistributionResponse, AppointmentDepartmentResponse, NewPatientStats, IAppointmentTypeDataResponse } from '../types';
import { IPersonalData, ReduxStore, IPatient, IDepartment } from '../../../../constants/types';
import { transformAppointmentsData, transformUniquePatientData, convertAppointmentTypeToChartData, convertNewPatientStatsToChartData } from '../services/transformers';
import { updatePatientsFromId } from '../../../../redux/actions/patientsActions';

interface IAppointmentByType {
  id: string;
  name: string;
  count: number;
}

export interface OutpatientsData {
  stats: OutPatientResponse;
  byDepartment: OutPatientResponse;
  diagnosis: ChartData;
  appointmentDistribution: DistributionResponse;
  totalPatients: ChartData,
  newPatients: NewPatientStats;
  patientsDetails: IPersonalData[];
  appointmentsByType: IAppointmentTypeDataResponse;
  overdueAppointments: AppointmentDepartmentResponse;
  appointmentsByDoctor: OutPatientResponse;
  appointementStats: OutPatientResponse;
  appointmentTypeData: any
  activityDistribution: ActivityPatientsDistributionResponse['stats'];
  waitTime: OutPatientResponse;
  appointmentsByInsurance: ChartData;
}


export const useOutpatientsData = (
  investigation: { uuid: string },
  departmentSelected: string | undefined,
  startDate: string,
  endDate: string
) => {
  const [data, setData] = useState<OutpatientsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Get patients data from Redux store using useSelector
  const investigationsData = useSelector((state: ReduxStore) => {
    const investigations = state.hospital.data.departments;
    if (!investigations) return [];
    return investigations as IDepartment[];
  }, shallowEqual); // Add shallowEqual from react-redux

  const patientsData = useSelector((state: ReduxStore) => {
    const currentInvestigation = state.investigations.currentInvestigation;
    if (!currentInvestigation) return [];
    return state.patients.data?.[currentInvestigation.uuid] as IPatient[] || [];
  }, shallowEqual);

  const dispatch = useDispatch();
  const patientsFetchedRef = useRef(false);

  // Fetch patients once, outside the main data effect
  useEffect(() => {
    if (!patientsData.length && !patientsFetchedRef.current && investigation?.uuid) {
      patientsFetchedRef.current = true;
      dispatch(updatePatientsFromId(investigation.uuid, true));
    }
  }, [investigation?.uuid, patientsData.length, dispatch]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchData = async () => {
      const uuidDepartment = departmentSelected || 'all';

      if (!investigation?.uuid || !startDate || !endDate) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const results = await Promise.allSettled([
          outpatientServices.getStatsOutpatients(investigation.uuid, uuidDepartment, startDate, endDate, abortController.signal),
          outpatientServices.getOutpatientsByDepartment(investigation.uuid, uuidDepartment, startDate, endDate, abortController.signal),
          outpatientServices.getDiagnosisWithAppointments(investigation.uuid, uuidDepartment, startDate, endDate, abortController.signal),
          outpatientServices.getAppointments(investigation.uuid, uuidDepartment, startDate, endDate, abortController.signal),
          outpatientServices.getTotalPatients(investigation.uuid, uuidDepartment, startDate, endDate, abortController.signal),
          outpatientServices.getNewPatientsRegistration(investigation.uuid, uuidDepartment, startDate, endDate, abortController.signal),
          outpatientServices.getOverdueAppointments(investigation.uuid, uuidDepartment, startDate, endDate, abortController.signal),
          outpatientServices.getAppointmentsByDoctor(investigation.uuid, uuidDepartment, startDate, endDate, abortController.signal),
          outpatientServices.getAppointmentStats(investigation.uuid, uuidDepartment, startDate, endDate, abortController.signal),
          outpatientServices.getAppointmentType(investigation.uuid, uuidDepartment, startDate, endDate, abortController.signal),
          outpatientServices.getActivity(investigation.uuid, uuidDepartment, startDate, endDate, abortController.signal),
          outpatientServices.getWaitTime(investigation.uuid, uuidDepartment, startDate, endDate, abortController.signal),
          outpatientServices.getAppointmentsByInsurance(investigation.uuid, uuidDepartment, startDate, endDate, abortController.signal)
        ]);

        const getValue = <T>(result: PromiseSettledResult<T>, fallback: T): T =>
          result.status === 'fulfilled' ? result.value : fallback;

        const emptyResponse = { status: 200, stats: {} } as OutPatientResponse;
        const emptyChartResponse = { status: 200, props: {} } as any;

        const statsResponse = getValue(results[0], emptyResponse);
        const departmentResponse = getValue(results[1], emptyResponse);
        const diagnosisResponse = getValue(results[2], { status: 200, stats: {}, patients: [] } as any);
        const appointmentsResponse = getValue(results[3], { status: 200, stats: {}, chartData: {} } as any);
        const totalPatientsResponse = getValue(results[4], emptyChartResponse);
        const newPatientsResponse = getValue(results[5], { status: 200, stats: {} } as any);
        const overdueAppointmentsResponse = getValue(results[6], { status: 200 } as any);
        const appointmentsByDoctorResponse = getValue(results[7], emptyResponse);
        const appointmentStatsResponse = getValue(results[8], emptyResponse);
        const appointmentTypeDataResponse = getValue(results[9], emptyChartResponse);
        const activityDistributionResponse = getValue(results[10], { status: 200, stats: {} } as any);
        const waitTimeResponse = getValue(results[11], emptyResponse);
        const appointmentsByInsuranceResponse = getValue(results[12], emptyChartResponse);

        // Filter patients based on diagnosisResponse.patients
        const relevantPatients = patientsData.filter(
          patient => diagnosisResponse.patients?.includes(patient.id)
        ).map(patient => patient.personalData);

        const relevantDepartments = appointmentsResponse.stats ? Object.keys(appointmentsResponse.stats).map(stat =>{
          const name = investigationsData.find(investigation => investigation.uuid === stat)?.name || 'no name';
          return {
            id: stat,
            name: name,
            count: appointmentsResponse.stats[stat]
          }
        }) : [];

        const appointmentTypeData = convertAppointmentTypeToChartData(relevantDepartments);

        const diagnosisChartData = transformUniquePatientData(diagnosisResponse);
        const patientResponseProps: ChartData = totalPatientsResponse && totalPatientsResponse.props ? totalPatientsResponse.props : {};

        const newPatientsStats = convertNewPatientStatsToChartData(newPatientsResponse.stats);

        setData({
          stats: statsResponse,
          byDepartment: departmentResponse,
          diagnosis: diagnosisChartData,
          appointmentDistribution: appointmentsResponse.chartData,
          totalPatients: patientResponseProps,
          newPatients: newPatientsStats,
          patientsDetails: relevantPatients,
          overdueAppointments: overdueAppointmentsResponse,
          appointmentsByType: appointmentTypeData,
          appointmentsByDoctor: appointmentsByDoctorResponse,
          appointementStats: appointmentStatsResponse,
          appointmentTypeData: appointmentTypeDataResponse as IAppointmentTypeDataResponse,
          activityDistribution:activityDistributionResponse.stats,
          waitTime: waitTimeResponse,
          appointmentsByInsurance: appointmentsByInsuranceResponse.props
        });

      } catch (err) {
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
        }

      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => abortController.abort();
  }, [investigation?.uuid, departmentSelected, startDate, endDate]);

  return { data, loading, error };
};